import { useCallback, useState, useRef } from "react";

const resolveInitialValue = (valueOrFactory) =>
  typeof valueOrFactory === "function" ? valueOrFactory() : valueOrFactory;

export const useLocalStorage = (key, initialValue) => {
  const initialRef = useRef(initialValue);

  const readValue = useCallback(() => {
    if (typeof window === "undefined") {
      return resolveInitialValue(initialRef.current);
    }
    try {
      const item = window.localStorage.getItem(key);
      if (item !== null) {
        return JSON.parse(item);
      }
    } catch (error) {
      console.warn(`useLocalStorage: failed reading key "${key}"`, error);
    }
    return resolveInitialValue(initialRef.current);
  }, [key]);

  const [storedValue, setStoredValue] = useState(() => readValue());

  const setValue = useCallback(
    (value) => {
      setStoredValue((prev) => {
        const valueToStore = value instanceof Function ? value(prev) : value;
        if (Object.is(prev, valueToStore)) {
          return prev;
        }
        if (typeof window !== "undefined") {
          try {
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
          } catch (error) {
            console.warn(`useLocalStorage: failed setting key "${key}"`, error);
          }
        }
        return valueToStore;
      });
    },
    [key],
  );

  return [storedValue, setValue];
};
